import { classifyMarketsByWindow } from "./marketTime";
import { isGenesisMarket } from "./genesisMarket";

export function normalizeLiveMarketSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) return null;
  const id = String(snapshot.id || snapshot.marketId || "").trim();
  if (!id) return null;

  const options = Array.isArray(snapshot.options) ? snapshot.options : [];
  const totalVotes = normalizeAmount(
    snapshot.totalVotes ?? options.reduce((sum, option) => sum + normalizeAmount(option?.votes ?? option?.totalVotes), 0)
  );

  const normalized = {
    ...snapshot,
    id,
    totalVotes,
    options: options.map((option) => {
      const votes = normalizeAmount(option?.votes ?? option?.totalVotes);
      return {
        ...option,
        id: String(option?.id || option?.optionId || ""),
        votes,
        percentage: totalVotes === 0 ? 0 : Math.round((votes / totalVotes) * 100)
      };
    })
  };

  // Ensure genesis indicator fields are present for consistent badge rendering on detail pages
  // (live fanout snapshots may lack them; client derivation guarantees parity with cards)
  if (isGenesisMarket(snapshot) || isGenesisMarket(normalized)) {
    normalized.requiresGenesisAccess = true;
    normalized.requires_genesis_access = true;
    normalized.accessPolicy = "genesis_only";
  }

  return normalized;
}

export function applyLiveMarketSnapshotToMarket(currentMarket, payload) {
  if (!currentMarket?.id) return currentMarket;
  const snapshot = extractLiveMarketSnapshots(payload).find((market) => market.id === currentMarket.id);
  if (!snapshot) return currentMarket;
  return mergeMarketSnapshot(currentMarket, snapshot);
}

export function applyLiveMarketSnapshotsToWindowGroups({
  active = [],
  upcoming = [],
  closed = [],
  snapshots,
  includeClosed = true,
  nowTs = Date.now()
} = {}) {
  const incomingSnapshots = extractLiveMarketSnapshots(snapshots);
  if (!incomingSnapshots.length) {
    return { active, upcoming, closed: includeClosed ? closed : [], changed: false };
  }

  const marketsById = new Map();
  const currentMarkets = includeClosed ? [...active, ...upcoming, ...closed] : [...active, ...upcoming];
  for (const market of currentMarkets) {
    if (market?.id) marketsById.set(market.id, market);
  }

  let changed = false;
  for (const snapshot of incomingSnapshots) {
    const current = marketsById.get(snapshot.id);
    marketsById.set(snapshot.id, mergeMarketSnapshot(current, snapshot));
    changed = true;
  }

  const grouped = classifyMarketsByWindow(Array.from(marketsById.values()), nowTs);
  return {
    ...grouped,
    closed: includeClosed ? grouped.closed : [],
    changed
  };
}

function extractLiveMarketSnapshots(payload) {
  const snapshots = Array.isArray(payload) ? payload : Array.isArray(payload?.markets) ? payload.markets : [payload];
  return snapshots.map((snapshot) => normalizeLiveMarketSnapshot(snapshot)).filter(Boolean);
}

function mergeMarketSnapshot(currentMarket, snapshot) {
  if (!currentMarket) return snapshot;
  const merged = {
    ...currentMarket,
    ...snapshot,
    options: mergeOptions(currentMarket.options, snapshot.options)
  };
  if (isGenesisMarket(currentMarket) || isGenesisMarket(snapshot) || isGenesisMarket(merged)) {
    merged.requiresGenesisAccess = true;
    merged.requires_genesis_access = true;
    merged.accessPolicy = "genesis_only";
  }
  return merged;
}

function mergeOptions(currentOptions = [], snapshotOptions = []) {
  const currentById = new Map();
  for (const option of Array.isArray(currentOptions) ? currentOptions : []) {
    if (option?.id) currentById.set(String(option.id), option);
  }
  return (Array.isArray(snapshotOptions) ? snapshotOptions : []).map((option) => ({
    ...(currentById.get(String(option?.id || "")) || {}),
    ...option
  }));
}

function normalizeAmount(value) {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount : 0;
}
